/**
 * The access period a pilot's designated inviter sets in `InviteAdvisorDialog`.
 *
 * That inviter's team invitation also gives the agent a seat on the inviter's subscription (see the
 * server's `pilotTeamSeatInvitation.ts`), so the dialog asks how long the seat lasts. The value has
 * the Add Seat dialog's shape so the same `SeatExpirationCard` picker renders it, and
 * `seatExpiresDateTime` is the end date the server stores on the seat, or null for no end date.
 * A chosen date that is not in the future is invalid, because the server refuses it.
 */
import { useCallback, useState } from 'react';
import type { ExpirationValue } from '@client/components/advisor-account-seats/SeatExpirationCard';
import { getExpirationDate } from '@client/components/advisor-account-seats/expirationUtils';

const NO_END_DATE: ExpirationValue = { mode: 'no_end_date', monthsFromToday: 0, specificDate: '' };

export function useInviteSeatPeriod() {
  const [expirationValue, setExpirationValue] = useState<ExpirationValue>(NO_END_DATE);

  const seatExpiresDateTime = getExpirationDate(expirationValue);
  const isValid =
    expirationValue.mode === 'no_end_date' ||
    (seatExpiresDateTime !== null && new Date(seatExpiresDateTime).getTime() > Date.now());

  const reset = useCallback(() => setExpirationValue(NO_END_DATE), []);

  return { expirationValue, setExpirationValue, seatExpiresDateTime, isValid, reset };
}
